(() => {
  const HISTORY_LIMIT = 8;

  class ProductDiscovery extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.tabs = Array.from(this.querySelectorAll('[data-discovery-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-discovery-panel]'));
      this.controls = this.querySelector('[data-discovery-controls]');
      this.previousButton = this.querySelector('[data-discovery-previous]');
      this.nextButton = this.querySelector('[data-discovery-next]');
      this.handleResize = () => this.updateAllProgress();
      this.showPrevious = () => this.moveActiveScroller(-1);
      this.showNext = () => this.moveActiveScroller(1);

      this.setupTabs();
      this.setupRecentlyViewed();
      this.setupScrollers();
      this.previousButton?.addEventListener('click', this.showPrevious);
      this.nextButton?.addEventListener('click', this.showNext);

      window.addEventListener('resize', this.handleResize, { passive: true });
      requestAnimationFrame(() => this.updateAllProgress());
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.handleResize);
      this.previousButton?.removeEventListener('click', this.showPrevious);
      this.nextButton?.removeEventListener('click', this.showNext);
      this.resizeObserver?.disconnect();
    }

    setupTabs() {
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.activateTab(tab.dataset.discoveryTab));
        tab.addEventListener('keydown', (event) => {
          let nextIndex = index;

          if (event.key === 'ArrowRight') nextIndex = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = this.tabs.length - 1;
          if (nextIndex === index && !['Home', 'End'].includes(event.key)) return;

          event.preventDefault();
          const nextTab = this.tabs[nextIndex];
          this.activateTab(nextTab.dataset.discoveryTab);
          nextTab.focus();
        });
      });
    }

    activateTab(name) {
      this.tabs.forEach((tab) => {
        const active = tab.dataset.discoveryTab === name;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
      });

      this.panels.forEach((panel) => {
        panel.hidden = panel.dataset.discoveryPanel !== name;
      });

      requestAnimationFrame(() => this.updateAllProgress());
    }

    setupRecentlyViewed() {
      const currentHandle = this.dataset.currentProductHandle;
      const currentTemplate = this.querySelector('template[data-current-product-card]');
      const recentList = this.querySelector('[data-recently-viewed-list]');
      const emptyMessage = this.querySelector('[data-recently-viewed-empty]');
      const history = this.readHistory();

      history
        .filter((entry) => entry.handle !== currentHandle)
        .slice(0, HISTORY_LIMIT)
        .forEach((entry) => {
          const card = this.createStoredCard(entry.html);
          if (card) recentList?.appendChild(card);
        });

      if (emptyMessage) emptyMessage.hidden = Boolean(recentList?.children.length);

      const currentCard = currentTemplate?.content.firstElementChild;
      if (!currentHandle || !currentCard) return;

      const updatedHistory = history.filter((entry) => entry.handle !== currentHandle);
      updatedHistory.unshift({
        handle: currentHandle,
        html: currentCard.outerHTML,
        viewedAt: Date.now(),
      });
      this.writeHistory(updatedHistory.slice(0, HISTORY_LIMIT));
    }

    readHistory() {
      try {
        const stored = JSON.parse(localStorage.getItem(this.dataset.storageKey) || '[]');
        if (!Array.isArray(stored)) return [];

        return stored.filter(
          (entry) =>
            entry &&
            typeof entry.handle === 'string' &&
            typeof entry.html === 'string' &&
            entry.html.length < 100000
        );
      } catch (_error) {
        return [];
      }
    }

    writeHistory(history) {
      try {
        localStorage.setItem(this.dataset.storageKey, JSON.stringify(history));
      } catch (_error) {
        // Recently viewed products are an enhancement; the page remains usable if storage is unavailable.
      }
    }

    createStoredCard(html) {
      const template = document.createElement('template');
      template.innerHTML = html.trim();
      const card = template.content.firstElementChild;

      if (!card?.matches('li.product-discovery__item[data-recent-product-card]')) return null;

      card.querySelectorAll('script, iframe, object, embed').forEach((element) => element.remove());
      card.querySelectorAll('*').forEach((element) => {
        Array.from(element.attributes).forEach((attribute) => {
          if (attribute.name.toLowerCase().startsWith('on')) element.removeAttribute(attribute.name);
        });
      });

      return card;
    }

    setupScrollers() {
      this.querySelectorAll('[data-discovery-scroller]').forEach((scroller) => {
        scroller.addEventListener('scroll', () => this.updateProgress(scroller), { passive: true });
      });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateAllProgress());
        this.querySelectorAll('[data-discovery-scroller]').forEach((scroller) => this.resizeObserver.observe(scroller));
      }
    }

    updateAllProgress() {
      this.querySelectorAll('[data-discovery-scroller]').forEach((scroller) => this.updateProgress(scroller));
    }

    moveActiveScroller(direction) {
      const activePanel = this.panels.find((panel) => !panel.hidden);
      const scroller = activePanel?.querySelector('[data-discovery-scroller]');
      const list = scroller?.querySelector('.product-discovery__list');
      const firstItem = list?.querySelector('.product-discovery__item');
      if (!scroller || !list || !firstItem) return;

      const gap = Number.parseFloat(getComputedStyle(list).columnGap) || 0;
      const distance = firstItem.getBoundingClientRect().width + gap;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      scroller.scrollBy({
        left: distance * direction,
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    }

    updateProgress(scroller) {
      if (scroller.closest('[data-discovery-panel]')?.hidden) return;

      const panel = scroller.closest('[data-discovery-panel]');
      const progress = panel?.querySelector('[data-discovery-progress]');
      const thumb = progress?.querySelector('[data-discovery-progress-thumb]');
      if (!progress || !thumb) return;

      const overflow = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      if (overflow < 2) {
        progress.hidden = true;
        thumb.style.width = '100%';
        thumb.style.left = '0%';
        if (this.controls) this.controls.hidden = true;
        if (this.previousButton) this.previousButton.disabled = true;
        if (this.nextButton) this.nextButton.disabled = true;
        return;
      }

      progress.hidden = false;
      if (this.controls) this.controls.hidden = false;
      const thumbWidth = Math.max(18, Math.min(100, (scroller.clientWidth / scroller.scrollWidth) * 100));
      const scrollProgress = Math.max(0, Math.min(1, scroller.scrollLeft / overflow));
      const thumbLeft = scrollProgress * (100 - thumbWidth);

      thumb.style.width = `${thumbWidth}%`;
      thumb.style.left = `${thumbLeft}%`;
      if (this.previousButton) this.previousButton.disabled = scroller.scrollLeft <= 1;
      if (this.nextButton) this.nextButton.disabled = scroller.scrollLeft >= overflow - 1;
    }
  }

  if (!customElements.get('product-discovery')) {
    customElements.define('product-discovery', ProductDiscovery);
  }
})();
